import styled, { css } from 'styled-components'
import { FaCheck, FaCircle, FaChevronRight } from 'react-icons/fa'

const textButtonStyles = css`
  color: #0082CE;
  text-decoration: underline;
  display: inline;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:focus {
    outline: none;
  }
  
  &:active {
    outline: none;
  }
`

export const Container = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  box-shadow: 0px 5px 10px rgba(0, 35, 75, 0.15);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  color: #486280;
  position: relative;
`

export const SuspendedContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const DisabledOverlay = styled.div`
  opacity: 0.25;
  pointer-events: none;
`

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  
  @media(max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

export const Title = styled.h2`
  color: #00234B;
  font-size: 20px;
  font-family: 'Playfair Display', serif;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
`

export const AssociateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
  
  @media(max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

export const Divider = styled.div`
  width: 1px;
  height: 100%;
  background: #979797;
  
  @media(max-width: 768px) {
    display: none;
  }
`

export const ProgressSection = styled.div`
  padding: 16px;
  background: #F4F7F8;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const LevelTracker = styled.div`
  display: flex;
  gap: 6px;
  padding: 8px 0;
`

export const LevelItem = styled.div`
  flex: 1;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border-radius: ${props => props.active ? '8px' : '0'};
  box-shadow: ${props => props.active ? '0px 2px 2px rgba(0, 0, 0, 0.10)' : 'none'};
  border: ${props => props.active ? '1px solid #0082CE' : 'none'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'white' : '#f8f9fa'};
  }
`

export const LevelLabel = styled.span`
  font-weight: 500;
  text-align: center;
`

export const LevelInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`

export const LevelIndicatorDot = styled(FaCircle)`
  color: #D5D7DB;
  width: 5px;
  height: 5px;
`

export const LevelCompleteIcon = styled(FaCheck)`
  color: #0082CE;
  width: 10px;
  height: 10px;
  font-weight: 600;
`

export const LevelInProgressIcon = styled(FaCircle)`
  color: #0082CE;
  width: 5px;
  height: 5px;
`

export const LevelWarningIcon = styled.img`
  width: 12px;
  height: 12px;
`

export const LevelLockIcon = styled.img`
  width: 10px;
  height: 10px;
`

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.completed ? '#D0E2FF' : '#D2DEE3'};
  border-radius: 64px;
  overflow: hidden;
  position: relative;
`

export const ProgressFill = styled.div`
  height: 100%;
  background: #0082CE;
  border-radius: 1000px;
  width: ${props => props.percentage}%;
`

export const ProgramInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

export const ProgramInfoLabel = styled.span`
  color: #6C757D;
  font-weight: 500;
  text-transform: uppercase;
`

export const ProgramInfoLink = styled.a`
  color: #0082CE;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    opacity: 0.8;
  }
`

export const RequirementsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

export const RequirementCardStyled = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${props => props.hasWarning ? '#DC994A' : '#D4D9DB'};
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const RequirementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const RequirementTitle = styled.h3`
  color: #00234B;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  margin: 0;
`

export const StatusBadgeSmall = styled.div`
  padding: 4px 12px;
  background: ${props => props.backgroundColor || '#E9EEF2'};
  border-radius: 32px;
  display: flex;
  align-items: center;
  gap: 4px;
`

export const StatusBadgeText = styled.span`
  color: ${props => props.textColor || '#404446'};
  font-weight: 500;
  line-height: 16px;
`

export const StatusBadgeCheckmark = styled(FaCheck)`
  color: ${props => props.color || '#2CA24B'};
  width: 10px;
  height: 10px;
  font-weight: 600;
`

export const StatusBadgeLock = styled.img`
  width: 16px;
  height: 16px;
`

export const RequirementDescription = styled.div`
  color: #486280;
  font-size: 12px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  line-height: 15px;
  margin: 0;
`

export const TextButton = styled.button`
  ${textButtonStyles};
`

export const RequirementInfoBanner = styled.div`
  padding: 6px 12px;
  background: #FFF8EC;
  border-radius: 8px;
  border: 1px solid #F3FAFD;
  display: flex;
  align-items: center;
`

export const RequirementInfoText = styled.p`
  flex: 1;
  padding: 4px 0;
  color: #CB6E00;
  font-weight: 500;
  line-height: 15px;
  margin: 0;
`


export const RequirementActions = styled.div`
  padding-top: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
`

export const ActionButton = styled.button`
  height: 24px;
  padding: 0 12px;
  border: 1px solid #0082CE;
  background: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    background: #f8f9fa;
    text-decoration: none;
  }
  
  &:active {
    outline: none;
    text-decoration: none;
  }
  
  &:visited {
    text-decoration: none;
  }
  
  &:focus {
    outline: none;
    text-decoration: none;
  }
`

export const ActionButtonText = styled.span`
  color: #0082CE;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`

export const PortalButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background: #0082CE;
  border: 1px solid #0082CE;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  
  &:hover {
    background: #006BA6;
  }
  
  &:focus {
    outline: 2px solid #00234B;
    outline-offset: 2px;
  }
`

export const PortalButtonText = styled.span`
  color: white;
  font-weight: 500;
  text-transform: uppercase;
`

export const AdditionalRequirementsSection = styled.div`
  background: #FFF8EC;
  border-radius: 8px;
  border: 1px solid #E8D5B2;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

export const AdditionalRequirementsHeader = styled.div`
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`

export const AdditionalRequirementsTitle = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const AdditionalRequirementsIcon = styled(FaChevronRight)`
  width: 16px;
  height: 16px;
  color: #00234B;
  transition: transform 0.2s ease;
`

export const AdditionalRequirementsTitleText = styled.h3`
  color: #00234B;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  margin: 0;
`

export const AdditionalRequirementsCount = styled.span`
  padding: 4px 12px;
  background: #FFEFD7;
  border-radius: 32px;
  color: #CB6E00;
  font-weight: 500;
  line-height: 16px;
`

export const AdditionalRequirementsBody = styled.div`
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const AdditionalRequirementsDescription = styled.p`
  color: #1F344B;
  font-size: 11px;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
`

export const AdditionalRequirementCard = styled.div`
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #DC994A;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const AdditionalRequirementHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`

export const AdditionalRequirementIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

export const AdditionalRequirementTitle = styled.h4`
  flex: 1;
  color: #00234B;
  font-size: 12px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  line-height: 18px;
  margin: 0;
`

export const AdditionalRequirementDescription = styled.p`
  padding: 4px 0;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
`

